export type AnalogLens = 'situational' | 'mechanical' | 'sector' | 'macro';

export type ForwardReturns = {
  t5?: number;
  t10?: number;
  t20?: number;
  t40?: number;
};

export type AnalogPricePoint = {
  t: number;
  price: number;
  /** Real trading session date when available (mechanical API). */
  date?: string;
};

export type AnalogSignificantMove = {
  date: string;
  percentChange: number;
  summary: string;
  primaryCatalyst?: string | null;
  sectorContext?: string | null;
  secondaryCatalysts?: string[] | null;
};

export type RelatedAnalogChart = {
  symbol: string;
  label: string;
  dayReturnPct: number;
  anchorClose: number;
  pricePath: AnalogPricePoint[];
  forwardReturns?: ForwardReturns;
  /** Statistically significant IXE / energy sector move dates visible on this chart window. */
  significantMoves?: AnalogSignificantMove[];
};

export type StockAnalogMatch = {
  date: string;
  similarityScore: number;
  dayReturnPct: number;
  anchorClose: number;
  headline: string;
  context: string;
  /** Short blurb on why this session is an analog of today. */
  whyThisMatches?: string | null;
  matchReasons: string[];
  /** Where the analog diverges from today — shown under "Where it differs". */
  keyDifferences?: string[];
  /** Session tape stats for visual fingerprint (SPY, QQQ, XLK, USO, VIX, y10). */
  dayStats?: Record<string, number> | null;
  whatHappenedNext: string;
  forwardReturns: ForwardReturns;
  pricePath: AnalogPricePoint[];
  /** When set (e.g. ^GSPC), mechanical charts use this index instead of the ticker. */
  chartSymbol?: string;
  /** Sector/commodity charts referenced by situational analog day_stats (e.g. USO, XLE, XLK). */
  relatedCharts?: RelatedAnalogChart[];
};

type RawStockAnalogMatch = Omit<StockAnalogMatch, 'pricePath'>;

export type StockAnalogLensData = {
  id: AnalogLens;
  label: string;
  subtitle: string;
  accent: 'emerald' | 'blue' | 'violet';
  description: string;
  /** Curated takeaway of what the analog set is generally saying. */
  aiSummary?: string | null;
  matches: StockAnalogMatch[];
  /** Lens-level chart symbol for market mechanical (^GSPC). */
  chartSymbol?: string;
};

export type StockAnalogSnapshot = {
  referenceDate: string;
  referenceDateLabel: string;
  ticker: string;
  companyName: string;
  close: number;
  dayReturnPct: number;
  catalystSummary: string;
  lenses: StockAnalogLensData[];
};

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

/** Build a rebased price path (T0 = 100) from the analog day's return and forward anchors. */
export function buildAnalogPricePath(
  dayReturnPct: number,
  forwardReturns: ForwardReturns,
): AnalogPricePoint[] {
  const prePopClose = 100 / (1 + dayReturnPct / 100);

  const anchors: Record<number, number> = {
    [-10]: prePopClose * 0.93,
    [-5]: prePopClose * 0.97,
    [-1]: prePopClose,
    [0]: 100,
    [5]: 100 + (forwardReturns.t5 ?? 0),
    [10]: 100 + (forwardReturns.t10 ?? 0),
    [20]: 100 + (forwardReturns.t20 ?? 0),
    [40]: 100 + (forwardReturns.t40 ?? forwardReturns.t20 ?? 0),
  };

  const sortedKeys = Object.keys(anchors)
    .map(Number)
    .sort((a, b) => a - b);

  const interpolate = (t: number): number => {
    if (anchors[t] != null) return anchors[t];
    let lower = sortedKeys[0];
    let upper = sortedKeys[sortedKeys.length - 1];
    for (let i = 0; i < sortedKeys.length - 1; i++) {
      if (t >= sortedKeys[i] && t <= sortedKeys[i + 1]) {
        lower = sortedKeys[i];
        upper = sortedKeys[i + 1];
        break;
      }
    }
    const fraction = (t - lower) / (upper - lower);
    return lerp(anchors[lower], anchors[upper], fraction);
  };

  const path: AnalogPricePoint[] = [];
  for (let t = -10; t <= 40; t++) {
    path.push({ t, price: interpolate(t) });
  }
  return path;
}

function withPricePaths(match: RawStockAnalogMatch): StockAnalogMatch {
  return {
    ...match,
    pricePath: buildAnalogPricePath(match.dayReturnPct, match.forwardReturns),
  };
}

function enrichSnapshot(
  snapshot: Omit<StockAnalogSnapshot, 'lenses'> & {
    lenses: Array<Omit<StockAnalogLensData, 'matches'> & { matches: RawStockAnalogMatch[] }>;
  },
): StockAnalogSnapshot {
  return {
    ...snapshot,
    lenses: snapshot.lenses.map((lens) => ({
      ...lens,
      matches: lens.matches.map(withPricePaths),
    })),
  };
}

const SVCO_MOCK_RAW: Omit<StockAnalogSnapshot, 'lenses'> & {
  lenses: Array<Omit<StockAnalogLensData, 'matches'> & { matches: RawStockAnalogMatch[] }>;
} = {
  referenceDate: '2026-07-08',
  referenceDateLabel: 'Wednesday, July 8, 2026',
  ticker: 'SVCO',
  companyName: 'Silvaco Group',
  close: 10.97,
  dayReturnPct: 10.14,
  catalystSummary:
    'Stock-specific rebound after July 6 Tech-X earnout share issuance was read as strategically positive; analyst Moderate Buy consensus and semi-sector dip-buying added tailwind.',
  lenses: [
    {
      id: 'situational',
      label: 'Situational',
      subtitle: 'Same type of catalyst',
      accent: 'emerald',
      description:
        'Finds prior sessions where SVCO moved on a similar fundamental trigger — M&A integration news, dilution reassessment, or analyst-driven sentiment shifts.',
      matches: [
        {
          date: '2025-04-29',
          similarityScore: 0.92,
          dayReturnPct: 4.52,
          anchorClose: 5.32,
          headline: 'Strategic acquisition of Tech-X Corporation',
          context:
            "Origin event for today's earnout. Same company, same Tech-X asset, same AI/photonics M&A narrative — strongest identity match available in history.",
          matchReasons: [
            'Shared themes: tech x deal, m and a, positive reassessment or rebound',
            "Origin event for today's earnout. Same company, same Tech-X asset, same AI/photonics M&A narrative.",
          ],
          whatHappenedNext:
            'Forward path not available in mock; live API attaches enriched OHLC forwards.',
          forwardReturns: { t5: -0.9, t10: -4.3, t20: 2.1, t40: 8.4 },
        },
        {
          date: '2025-05-02',
          similarityScore: 0.78,
          dayReturnPct: 5.87,
          anchorClose: 5.59,
          headline: 'Tech-X follow-through rebound (3 sessions later)',
          context:
            'Closest pattern twin: delayed positive pricing of the Tech-X story without a fresh same-day disclosure — mirrors today\'s post-filing reassessment.',
          matchReasons: [
            'Shared themes: tech x deal, m and a, positive reassessment or rebound',
            'Origin: 2025-04-29 — Acquisition of Tech-X Corporation (3 trading days prior)',
          ],
          whatHappenedNext:
            'Forward path not available in mock; live API attaches enriched OHLC forwards.',
          forwardReturns: { t5: -4.3, t10: 1.2, t20: 6.5, t40: 11.0 },
        },
        {
          date: '2026-06-01',
          similarityScore: 0.68,
          dayReturnPct: 10.46,
          anchorClose: 10.67,
          headline: 'Q1 follow-through: +26% revenue, AI FTCO, path to profitability',
          context:
            "Matches today's secondary stack (Q1 +26% revenue, AI FTCO, Q2 profitability guidance) with a nearly identical +10% move.",
          matchReasons: [
            'Shared themes: positive reassessment or rebound, analyst or earnings ai ftco secondary',
            'Nearly identical +10% single-day magnitude',
          ],
          whatHappenedNext:
            'Forward path not available in mock; live API attaches enriched OHLC forwards.',
          forwardReturns: { t5: 14.1, t10: 3.2, t20: -5.4, t40: -8.1 },
        },
        {
          date: '2025-08-01',
          similarityScore: 0.62,
          dayReturnPct: -1.12,
          anchorClose: 4.4,
          headline: 'Consummation of stock purchase agreement with Mixel Group',
          context:
            "Closest mechanical analogue to today's July 6 8-K: post-announcement deal consummation / equity settlement paperwork.",
          matchReasons: [
            'Shared themes: m and a, post deal settlement or share issuance',
            'Gap vs today: Different target (Mixel, not Tech-X)',
          ],
          whatHappenedNext:
            'Forward path not available in mock; live API attaches enriched OHLC forwards.',
          forwardReturns: { t5: 17.2, t10: 8.4, t20: -2.1, t40: -6.5 },
        },
        {
          date: '2025-07-29',
          similarityScore: 0.55,
          dayReturnPct: -1.74,
          anchorClose: 4.52,
          headline: 'Silvaco announced the acquisition of Mixel Group, Inc.',
          context:
            'Same structural catalyst bucket — Silvaco expanding the product stack via acquisition — but at announcement stage rather than earnout settlement.',
          matchReasons: [
            'Shared themes: m and a',
            'Gap vs today: Announcement, not post-closing share issuance',
          ],
          whatHappenedNext:
            'Forward path not available in mock; live API attaches enriched OHLC forwards.',
          forwardReturns: { t5: 0.7, t10: 17.2, t20: 4.8, t40: -3.2 },
        },
      ],
    },
    {
      id: 'mechanical',
      label: 'Mechanical',
      subtitle: 'Same price shape',
      accent: 'blue',
      description:
        'Matches on OHLC fingerprint — a sharp +10% bounce while still down over the prior 20 sessions, RSI near neutral after a washout.',
      matches: [
        {
          date: '2025-06-03',
          similarityScore: 0.94,
          dayReturnPct: 10.31,
          anchorClose: 7.45,
          headline: 'V-shaped bounce off 20-day low',
          context:
            'Identical setup: −18% over 20 days, RSI ~45, then a +10% engulfing day on 2.4× average volume.',
          matchReasons: [
            '20-day return within 2% of today',
            'RSI (14) ~44 vs today ~46',
            'Bounce magnitude within 0.2% of today',
          ],
          whatHappenedNext:
            'Classic “dead cat” profile — +5 days +8%, then −22% by day 30 as downtrend resumed.',
          forwardReturns: { t5: 8.3, t10: 3.7, t20: -9.4, t40: -22.1 },
        },
        {
          date: '2024-12-11',
          similarityScore: 0.88,
          dayReturnPct: 9.56,
          anchorClose: 6.8,
          headline: 'Gap-and-go off support shelf',
          context:
            'Price had consolidated for 6 sessions at a prior breakdown level before a volume surge breakout — same mechanical template.',
          matchReasons: [
            '6-day base before breakout (today had 5)',
            'Close in top 15% of daily range',
            'Volume z-score > 2.0',
          ],
          whatHappenedNext:
            'Trend follow-through for 3 weeks (+19% peak) before failing at prior resistance.',
          forwardReturns: { t5: 6.2, t10: 11.4, t20: 19.1, t40: 8.7 },
        },
        {
          date: '2026-01-15',
          similarityScore: 0.81,
          dayReturnPct: 11.02,
          anchorClose: 9.95,
          headline: 'Oversold hammer into reversal',
          context:
            'Three consecutive down days flushed weak hands; today’s analog shows the same hammer-then-surge sequence.',
          matchReasons: [
            'Prior 3-day streak ≤ −4% each',
            'Low of day within 1% of 20-day low',
            'Close above prior day high',
          ],
          whatHappenedNext:
            'Choppy sideways for 10 days, then another leg down on sector-wide semi unwind.',
          forwardReturns: { t5: 1.8, t10: -2.3, t20: -8.6, t40: -15.2 },
        },
      ],
    },
    {
      id: 'sector',
      label: 'Sector',
      subtitle: 'Same semi backdrop',
      accent: 'violet',
      description:
        'Ranks historical SOX / semiconductor sessions by how closely their sector backdrop matches today — buy-the-dip / AI de-risking / fundamentals-intact rhymes on SOX, not SVCO’s own catalyst story.',
      matches: [
        {
          date: '2025-11-05',
          similarityScore: 0.92,
          dayReturnPct: 3.02,
          anchorClose: 7190.266,
          headline: 'Buy-the-dip in chipmakers after a sharp AI-valuation selloff',
          context:
            'Closest overall joint match: +3.0% bounce after a −4.0% AI-valuation de-risking day, with the same buy-the-dip / stretched-multiples / AI-memory narrative as today.',
          matchReasons: [
            'Shared sector themes: buy the dip rebound, ai chip de risking prior day, profit taking after parabolic run, ai memory fundamentals intact',
            'Closest overall joint match: +3.0% bounce after a −4.0% AI-valuation de-risking day.',
          ],
          whatHappenedNext:
            'Choppy near-term (−1.5% / 5d, −7.2% / 10d), then recovered to +10.8% / 60d.',
          forwardReturns: { t5: -1.5, t10: -7.24, t20: 0.36, t40: 3.56 },
        },
        {
          date: '2025-12-18',
          similarityScore: 0.88,
          dayReturnPct: 2.51,
          anchorClose: 6863.6286,
          headline: 'Relief rebound after AI-hardware de-risking, with Micron/AI demand confirming fundamentals',
          context:
            "Best 'fundamentals intact' relief day: prior session was an AI-capex/valuation unwind; bounce day reset sentiment that AI hardware demand was still real (Micron beat/guide).",
          matchReasons: [
            'Shared sector themes: buy the dip rebound, ai chip de risking prior day, profit taking after parabolic run, ai memory fundamentals intact',
            "Best 'fundamentals intact' relief day after AI-capex/valuation unwind.",
          ],
          whatHappenedNext: 'Strong continuation: +5.0% / 5d, +13.6% / 20d, +13.6% / 60d.',
          forwardReturns: { t5: 5.01, t10: 8.49, t20: 13.56, t40: 19.68 },
        },
        {
          date: '2025-11-21',
          similarityScore: 0.85,
          dayReturnPct: 0.86,
          anchorClose: 6406.432,
          headline: 'Deep-drawdown AI-unwind bounce after a failed Nvidia relief day',
          context:
            "Closest drawdown twin (−13.3% vs today's −14.2%). Prior day was a failed Nvidia relief rally into a broader AI-valuation unwind; bounce came with a Fed/macro assist.",
          matchReasons: [
            'Shared sector themes: buy the dip rebound, ai chip de risking prior day, deep drawdown from highs, ai valuation unwind',
            'Closest drawdown twin (−13.3% vs today −14.2%).',
          ],
          whatHappenedNext: 'Best near-term of the set: +9.6% / 5d, +11.5% / 20d, +28.9% / 60d.',
          forwardReturns: { t5: 9.59, t10: 15.12, t20: 11.54, t40: 25.74 },
        },
        {
          date: '2026-03-09',
          similarityScore: 0.82,
          dayReturnPct: 3.93,
          anchorClose: 7810.3995,
          headline: 'Iran/Hormuz risk-premium fade sparked a chip rebound after an AI de-risking selloff',
          context:
            "Best geopolitical rhyme for today's Hormuz/Iran backdrop. Prior session sold AI chips on policy + risk-off; bounce day saw Iran risk premium fade and chips snap back.",
          matchReasons: [
            'Shared sector themes: buy the dip rebound, ai chip de risking prior day, iran hormuz geopolitics, oil risk premium',
            "Best geopolitical rhyme for today's Hormuz/Iran backdrop.",
          ],
          whatHappenedNext: 'Flat near-term (−0.2% / 5d, +2.5% / 20d), then a large later rally (+78.2% / 60d).',
          forwardReturns: { t5: -0.18, t10: -0.48, t20: 2.48, t40: 40.59 },
        },
        {
          date: '2024-07-22',
          similarityScore: 0.74,
          dayReturnPct: 4.0,
          anchorClose: 5477.8294,
          headline: 'Classic post-selloff SOX rebound led by Nvidia after geopolitics/trade-restriction fears',
          context:
            "Strongest pure mechanical 'semis bounce after geopolitics/AI scare' from the earlier AI cycle — cautionary case where the bounce failed.",
          matchReasons: [
            'Shared sector themes: buy the dip rebound, semiconductor sector bounce, geopolitics trade scare prior, nvidia ai leadership',
            'Cautionary case: bounce failed into the July–Aug 2024 chip correction.',
          ],
          whatHappenedNext: 'Bounce failed: −7.1% / 5d, −17.5% / 10d, −3.9% / 20d, −0.8% / 60d.',
          forwardReturns: { t5: -7.12, t10: -17.5, t20: -3.86, t40: -10.33 },
        },
      ],
    },
  ],
};

const SVCO_MOCK = enrichSnapshot(SVCO_MOCK_RAW);

const GENERIC_MOCK_RAW: Omit<StockAnalogSnapshot, 'ticker' | 'companyName' | 'lenses'> & {
  lenses: Array<Omit<StockAnalogLensData, 'matches'> & { matches: RawStockAnalogMatch[] }>;
} = {
  referenceDate: '2026-07-08',
  referenceDateLabel: 'Wednesday, July 8, 2026',
  close: 100,
  dayReturnPct: 3.5,
  catalystSummary: 'Mixed session — stock moved on company-specific news with sector providing secondary context.',
  lenses: SVCO_MOCK_RAW.lenses.map((lens) => ({
    ...lens,
    matches: lens.matches.map((m) => ({
      ...m,
      headline: m.headline.replace(/SVCO/g, 'Ticker'),
      context: m.context.replace(/SVCO/g, 'the stock'),
    })),
  })),
};

export function getStockAnalogMock(ticker: string, companyName?: string): StockAnalogSnapshot {
  const upper = ticker.toUpperCase();
  if (upper === 'SVCO') {
    return SVCO_MOCK;
  }
  return enrichSnapshot({
    ...GENERIC_MOCK_RAW,
    ticker: upper,
    companyName: companyName ?? upper,
  });
}

type SnapshotHeader = Partial<
  Pick<
    StockAnalogSnapshot,
    'referenceDate' | 'referenceDateLabel' | 'close' | 'dayReturnPct' | 'catalystSummary' | 'companyName'
  >
>;

/** Merge a live mechanical lens into the mock snapshot (situational/sector stay mocked). */
export function mergeMechanicalLens(
  base: StockAnalogSnapshot,
  mechanical: StockAnalogLensData,
  header?: SnapshotHeader,
): StockAnalogSnapshot {
  return {
    ...base,
    ...header,
    ticker: base.ticker,
    companyName: header?.companyName ?? base.companyName,
    lenses: base.lenses.map((lens) => (lens.id === 'mechanical' ? mechanical : lens)),
  };
}

/** Merge a live situational lens (catalyst analogs) into the snapshot. */
export function mergeSituationalLens(
  base: StockAnalogSnapshot,
  situational: StockAnalogLensData,
  header?: SnapshotHeader,
): StockAnalogSnapshot {
  return {
    ...base,
    ...header,
    ticker: base.ticker,
    companyName: header?.companyName ?? base.companyName,
    // Prefer live situational catalyst summary when present; keep mechanical header fields if already set.
    catalystSummary: header?.catalystSummary ?? base.catalystSummary,
    lenses: base.lenses.map((lens) => (lens.id === 'situational' ? situational : lens)),
  };
}

/** Merge a live sector lens (SOX / industry backdrop analogs) into the snapshot. */
export function mergeSectorLens(
  base: StockAnalogSnapshot,
  sector: StockAnalogLensData,
  header?: SnapshotHeader,
): StockAnalogSnapshot {
  return {
    ...base,
    ...header,
    ticker: base.ticker,
    companyName: header?.companyName ?? base.companyName,
    lenses: base.lenses.map((lens) => (lens.id === 'sector' ? sector : lens)),
  };
}
